<template>
  <div>
    <v-breadcrumbs :items="items"></v-breadcrumbs>
    <form v-on:submit.prevent="submitForm">
      <v-row>
        <v-col cols="8">
          <v-row>
            <v-col cols="6">
              <v-card dark :loading="loading">
                <v-card-title>
                  Patient Info
                  <v-spacer></v-spacer>
                </v-card-title>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                    <tr>
                      <td>Patient ID</td>
                      <td>:</td>
                      <td class="">{{ patient_info.patient_id }}</td>
                    </tr>
                    <tr>
                      <td>Name</td>
                      <td>:</td>
                      <td class="">{{ patient_info.patient_name }}</td>
                    </tr>
                    <tr>
                      <td>Age</td>
                      <td>:</td>
                      <td class="">{{ patient_info.patient_age }}</td>
                    </tr>
                    <tr>
                      <td>Phone</td>
                      <td>:</td>
                      <td class="">{{ patient_info.patient_phone }}</td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>
            </v-col>
            <v-col cols="6">
              <v-card dark :loading="loading" :color="isOperationCancelled ? 'error' : null">
                <v-card-title>
                  Surgery Info
                  <v-spacer></v-spacer>
                  <v-chip v-if="isOperationCancelled" small label color="white" text-color="error">
                    CANCELLED
                  </v-chip>
                </v-card-title>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                    <tr>
                      <td>Name</td>
                      <td>:</td>

                      <td class="">{{ operation.name }}</td>
                    </tr>
                    <tr>
                      <td>Eye Type</td>
                      <td>:</td>
                      <td class="">{{ op_registration.eye_type }}</td>
                    </tr>
                    <tr>
                      <td>Operation Type</td>
                      <td>:</td>
                      <td class="">{{ operation.op_type }}</td>
                    </tr>
                    <tr>
                      <td>Doctor</td>
                      <td>:</td>
                      <td class="">{{ doctor.dr_name }}</td>
                    </tr>
                    <tr>
                      <td>Operation Date</td>
                      <td>:</td>
                      <td class="">{{ operation_date }}</td>
                    </tr>
                    <tr>
                      <td>Registration No</td>
                      <td>:</td>
                      <td class="">{{ op_registration.registration_no }}</td>
                    </tr>
                    <tr>
                      <td>Status</td>
                      <td>:</td>
                      <td class="">{{ op_registration.opp_status }}</td>
                    </tr>
                    <tr v-if="op_registration.schedule_id">
                      <td>Schedule ID</td>
                      <td>:</td>
                      <td class="">{{ op_registration.schedule_id }}</td>
                    </tr>
                    <tr v-if="op_registration.schedule_id && op_registration.ot_schedule_data">
                      <td>Schedule Status</td>
                      <td>:</td>
                      <td class="">{{ op_registration.ot_schedule_data.status }}</td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>
            </v-col>
          </v-row>
          <v-card>
            <v-card-title>
              OT Bill: Refund to Customer
              <v-spacer></v-spacer>
            </v-card-title>
            <v-col cols="12">
              <v-alert v-if="isOperationCancelled" type="error" dense>
                This operation has been CANCELLED. No refunds can be processed.
                <template v-if="op_registration.cancel_description">
                  <br><strong>Reason:</strong> {{ op_registration.cancel_description }}
                </template>
              </v-alert>

              <v-alert v-else-if="isOperationDateOver && !isAdmin" type="error" dense>
                Cannot refund after operation date ({{ operation_date }})
              </v-alert>

              <v-alert v-else-if="isOperationDateOver && isAdmin" type="info" dense>
                Super admin can process refund after operation date.
              </v-alert>

              <v-simple-table dense class="mb-4">
                <template v-slot:default>
                  <tbody>
                  <tr>
                    <td><strong>Total Paid Amount</strong></td>
                    <td class="text-right">{{ formatCurrency(total_paid_amount) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Previous Refund Amount</strong></td>
                    <td class="text-right">{{ formatCurrency(previous_refund_amount) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Available for Refund</strong></td>
                    <td class="text-right success--text">{{ formatCurrency(available_refund_amount) }}</td>
                  </tr>
                  <tr v-if="invoice_total !== total_paid_amount">
                    <td><strong>Invoice Total</strong></td>
                    <td class="text-right">{{ formatCurrency(invoice_total) }}</td>
                  </tr>
                  <tr>
                    <td><strong>OT Bill Amount</strong></td>
                    <td class="text-right">{{ formatCurrency(op_registration.ot_amount) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Due Balance</strong></td>
                    <td class="text-right">{{ formatCurrency(op_registration.due_amount) }}</td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-text-field
                  v-model="refund_amount"
                  label="Refund Amount"
                  type="number"
                  min="0"
                  :max="available_refund_amount"
                  :hint="maxRefundHint"
                  persistent-hint
                  outlined
                  :readonly="!canSubmitRefund"
                  :disabled="!canSubmitRefund"
                  :rules="[amountRule]"
              ></v-text-field>
              <v-autocomplete
                  v-model="refund_by_user_id"
                  :items="users"
                  item-text="full_name"
                  item-value="id"
                  label="Refund Paid By (Staff who paid patient)"
                  placeholder="Select staff who paid refund amount"
                  outlined
                  dense
                  clearable
                  :readonly="!canSubmitRefund"
                  :disabled="!canSubmitRefund"
                  :rules="[v => !!v || 'Please select who paid the refund amount']"
              ></v-autocomplete>
              <v-textarea
                  color="teal"
                  v-model="refund_note"
                  outlined
                  class="mt-2"
                  :readonly="!canSubmitRefund"
                  :disabled="!canSubmitRefund"
              >
                <template v-slot:label>
                  <div>Refund Note</div>
                </template>
              </v-textarea>

              <!-- Checkbox: Cancel operation + linked OT schedule on full refund -->
              <v-checkbox
                  v-if="show_cancel_checkbox && !isOperationCancelled"
                  v-model="cancel_operation"
                  label="Cancel Operation and OT Schedule with Full Refund"
                  color="warning"
                  :readonly="!canSubmitRefund"
                  :disabled="!canSubmitRefund"
                  class="mt-0"
              ></v-checkbox>

              <!-- Warning when checkbox checked but not full refund -->
              <v-alert
                  v-if="cancel_operation && !isFullRefund && !isOperationCancelled"
                  type="warning"
                  dense
                  class="mt-2"
              >
                Checkbox will only apply when refund amount equals available amount ({{ formatCurrency(available_refund_amount) }})
              </v-alert>
              <v-alert
                  v-if="cancel_operation && isFullRefund && op_registration.schedule_id && !isOperationCancelled"
                  type="info"
                  dense
                  class="mt-2"
              >
                Full refund will cancel this operation and the linked OT schedule (ID: {{ op_registration.schedule_id }}).
              </v-alert>

              <div class="text-right">
                <v-btn small color="primary" type="submit" :disabled="!canSubmitRefund">Submit</v-btn>
              </div>
            </v-col>
          </v-card>
        </v-col>

        <v-col cols="4">

          <v-card :loading="loading">
            <v-card-title>
              Invoice Details
              <v-spacer></v-spacer>
            </v-card-title>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                <tr>
                  <th class="text-left">Invoice ID</th>
                  <th class="text-left">Paid Amount</th>
                  <th class="text-left">Date</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in invoices" :key="item.invoice_id">
                  <td>{{ item.invoice_id }}</td>
                  <td class="text-right">{{ item.paid_amount }}</td>
                  <td>{{ item.create_date }}</td>
                </tr>
                <tr>
                  <td>Total</td>
                  <th class="text-right">{{ formatCurrency(invoice_total) }}</th>
                  <td></td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card :loading="loading">
            <v-card-title>
              Refund History ({{ refund_history.length }})
              <v-spacer></v-spacer>
              <span class="error--text font-weight-bold">Total: {{ formatCurrency(previous_refund_amount) }}</span>
            </v-card-title>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Refunded By</th>
                  <th>Note</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(refund, index) in refund_history" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ formatDate(refund.refund_date) }}</td>
                  <td class="error--text">{{ formatCurrency(refund.amount) }}</td>
                  <td>{{ refund.description || '-' }}</td>
                  <td>{{ refund.user_name }}</td>
                  <td>{{ refund.note || '-' }}</td>
                </tr>
                <tr v-if="refund_history.length === 0">
                  <td colspan="6" class="text-center grey--text">No refunds yet</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </form>
  </div>
</template>
<style scoped>
.layout {
  display: inline-block;
  width: 100%;
}
</style>
<script>
import atshq from "../../../../js/setup";
import "vuejs-noty/dist/vuejs-noty.css";
import PermissionName from "@/js/PermissionName";

export default {
  data() {
    return {
      OT_BILL_REFUND: PermissionName.OT_BILL_REFUND,
      patient_info: { patient_name: null },
      doctor: { patient_name: null },
      operation: { patient_name: null },
      op_registration: { patient_name: null },
      op_reg_id: this.$route.params.id,
      sureonList: [],
      refund_amount: 0,
      refund_by_user_id: null,
      users: [],
      refund_note: "",
      cancel_operation: false,
      show_cancel_checkbox: false, // Track if checkbox should be shown
      invoiceRefund: "",
      loading: true,
      user_id: localStorage.getItem("userId"),
      local_ip_address: localStorage.getItem("local_ip_address"),
      headers: [
        {
          text: "Invoice ID",
          align: "start",
          value: "invoice_id",
        },
        { text: "Amoount", value: "paid_amount" },
        { text: "Date", value: "create_date" },
      ],
      invoices: [],
      errors: [],
      invoice_total: 0,
      totalRefund: 0,
      left_paid_amount:0,
      total_paid_amount: 0,
      previous_refund_amount: 0,
      available_refund_amount: 0,
      operation_date: null,
      refund_history: [],
      rules: [
        (value) => !!value || "Required.",
        (value) => (value && value.length >= 1) || "Min 3 characters",
      ],
      numberRule: (val) => {
        if (val < 0) return "Please enter a positive number";
        return true;
      },
      amountRule: (val) => {
        const amount = parseFloat(val);
        const available = parseFloat(this.available_refund_amount || 0);
        if (!amount || amount <= 0) return "Please enter a valid amount";
        if (amount > available) {
          return "Refund cannot exceed available amount (" + available.toFixed(2) + ")";
        }
        return true;
      },
      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "Ot Bill Refund",
          disabled: true,
          href: "patients",
        },
      ],
    };
  },
  // Fetches posts when the component is created.
  created() {
    this.getUsers();
    this.initialize();
  },
  mounted() {
    if (!this.checkPermissionHasOrNot(this.OT_BILL_REFUND)) {
      this.$router.push('/error/403');
    }
  },
  computed: {
    loginUserId() {
      const localId = localStorage.getItem("userId");
      return parseInt(localId, 10) || null;
    },
    isAdmin() {
      return localStorage.getItem("user_role") === '1';
    },
    isOperationDateOver() {
      if (!this.operation_date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const oppDate = new Date(this.operation_date);
      return today > oppDate;
    },
    isOperationCancelled() {
      return this.op_registration && this.op_registration.opp_status === 'cancel';
    },
    isFullRefund() {
      return parseFloat(this.refund_amount) >= parseFloat(this.available_refund_amount);
    },
    canSubmitRefund() {
      if (!this.checkPermissionHasOrNot(this.OT_BILL_REFUND)) {
        return false;
      }
      if (this.isOperationCancelled) {
        return false;
      }
      if (this.isOperationDateOver && !this.isAdmin) {
        return false;
      }
      return true;
    },
    maxRefundHint() {
      return 'Max available: ' + this.formatCurrency(this.available_refund_amount);
    },
  },
  methods: {
    getUsers() {
      atshq
          .get("/users/user?active=1")
          .then((response) => {
            this.users = response.data.data || [];
          })
          .catch((e) => {
            this.errors.push(e);
          });
    },
    initialize() {
      // alert(this.op_reg_id);
      atshq
          .get("ot-bill/create?reg_id=" + this.op_reg_id)
          .then((response) => {
            // JSON responses are automatically parsed.
            this.patient_info = response.data.data.patient;
            this.invoices = response.data.data.invoice_list;
            this.doctor = response.data.data.doctor;
            this.operation = response.data.data.operation_details;
            this.op_registration = response.data.data.op_registration;
            this.sureonList = response.data.data.doctorlist;
            this.invoiceRefund = response.data.data.invoice_refund;
            this.loading = false;

            this.invoice_total = this.invoices.reduce(
                (acc, item) => acc + parseFloat(item.paid_amount || 0),
                0
            );
            this.totalRefund = this.invoiceRefund.reduce(
                (acc, item) => acc + parseFloat(item.refund_amount || 0),
                0
            );

            // Set operation date
            this.operation_date = this.op_registration.opp_date;

            this.refund_history = this.buildRefundHistory();

            // Use operation_patient.paid_amount (same as backend validation)
            const regPaid = parseFloat(this.op_registration.paid_amount || 0);
            this.total_paid_amount = regPaid > 0 ? regPaid : this.invoice_total;
            this.previous_refund_amount = parseFloat(this.op_registration.refund_amount || 0);
            this.available_refund_amount = Math.max(
                0,
                this.total_paid_amount - this.previous_refund_amount
            );

            // Default empty so user must choose who paid refund (not auto login user)
            this.refund_amount = this.available_refund_amount > 0 ? "" : 0;
            this.cancel_operation = false;

            this.show_cancel_checkbox = this.available_refund_amount > 0 && this.op_registration.opp_status !== 'cancel';
          })
          .catch((e) => {
            this.errors.push(e);
          });
    },
    parseRefundRemarks(remarks, item) {
      const text = (remarks || "").trim();
      let description = "";
      let note = "";

      const descMatch = text.match(/Description:\s*(.*?)(?:\s*\|\s*Note:|\s*\|\s*Refunded By:|$)/i);
      const noteMatch = text.match(/Note:\s*(.*?)(?:\s*\|\s*Refunded By:|$)/i);
      if (descMatch || noteMatch) {
        description = descMatch ? descMatch[1].trim() : "";
        if (/OT\s+Refund/i.test(description)) {
          description = /Full/i.test(description) ? "Full refund" : "Partial refund";
        }
        note = noteMatch ? noteMatch[1].trim() : "";
      } else if (/^(Full|Partial)\s+refund$/i.test(text)) {
        description = text;
      } else if (text) {
        const amt = parseFloat(item && item.refund_amount ? item.refund_amount : 0);
        const paid = parseFloat(this.total_paid_amount || 0);
        description = amt >= paid && paid > 0 ? "Full refund" : "Partial refund";
        note = text;
      } else if (item && item.refund_amount) {
        const amt = parseFloat(item.refund_amount);
        const paid = parseFloat(this.total_paid_amount || 0);
        description = amt >= paid && paid > 0 ? "Full refund" : "Partial refund";
      }

      return { description, note };
    },
    buildRefundHistory() {
      if (this.invoiceRefund && this.invoiceRefund.length) {
        return this.invoiceRefund.map((item) => {
          const parsed = this.parseRefundRemarks(item.remarks, item);
          return {
            refund_date: item.refund_date || item.created_at,
            amount: item.refund_amount,
            user_name: item.user
                ? (item.user.full_name || item.user.username)
                : "-",
            description: parsed.description,
            note: parsed.note,
          };
        });
      }

      if (this.op_registration.refund_note) {
        try {
          const parsed = JSON.parse(this.op_registration.refund_note);
          const list = Array.isArray(parsed) ? parsed : [parsed];
          return list.map((item) => ({
            refund_date: item.refund_date,
            amount: item.amount,
            user_name: item.user_name || "-",
            description: item.description || "",
            note: item.note || "",
          }));
        } catch (e) {
          return [];
        }
      }

      return [];
    },
    submitForm() {
      if (!this.canSubmitRefund) {
        if (!this.checkPermissionHasOrNot(this.OT_BILL_REFUND)) {
          this.$noty.error("You do not have permission to process OT bill refunds.");
        } else if (this.isOperationCancelled) {
          this.$noty.error("This operation has been cancelled. Refund is not allowed.");
        } else if (this.isOperationDateOver && !this.isAdmin) {
          this.$noty.error("Cannot refund after operation date (" + this.operation_date + ").");
        }
        return;
      }

      if (!this.loginUserId) {
        this.$noty.error("User session not found. Please login again.");
        return;
      }

      if (!this.refund_by_user_id) {
        this.$noty.error("Please select staff who paid the refund amount to the patient.");
        return;
      }

      const refundAmount = parseFloat(this.refund_amount);
      const availableAmount = parseFloat(this.available_refund_amount || 0);

      if (!refundAmount || refundAmount <= 0) {
        this.$noty.error("Please enter a valid refund amount.");
        return;
      }

      if (refundAmount > availableAmount) {
        this.$noty.error("Refund amount cannot exceed available amount (" + availableAmount.toFixed(2) + ").");
        return;
      }

      // Check if this is a full refund
      const isFullRefund = refundAmount >= availableAmount;

      // Cancel checkbox only applies on full refund; clear if user chose partial amount
      if (this.cancel_operation && !isFullRefund) {
        this.cancel_operation = false;
      }

      let confirmMsg = "Are you sure?";
      if (isFullRefund && this.cancel_operation) {
        const scheduleMsg = this.op_registration.schedule_id
            ? "\n- OT schedule (ID: " + this.op_registration.schedule_id + ") will be cancelled"
            : "";
        confirmMsg =
            "Full refund will:\n" +
            "- Refund the patient\n" +
            "- Cancel this operation" +
            scheduleMsg +
            "\n\nAre you sure?";
      }
      if (!confirm(confirmMsg)) {
        return;
      }

      atshq
          .post("ot-bill-refund", {
            op_reg_id: this.op_reg_id,
            refund_amount: refundAmount,
            refund_note: (this.refund_note || "").trim(),
            refund_by_user_id: parseInt(this.refund_by_user_id, 10),
            login_user_id: this.loginUserId,
            cancel_operation: this.cancel_operation ? 1 : 0,
          })
          .then((response) => {
            let msg = response.data.message;
            if (response.data.schedule_cancelled) {
              msg += " Schedule is now read-only on schedule-create.";
            }
            this.$noty.success(msg);
            this.$router.go(this.$router.currentRoute);
          })
          .catch((e) => {
            this.$noty.error(e.response.data.message);
            this.errors.push(e);
          })
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString();
    },
    formatCurrency(value) {
      const num = parseFloat(value || 0);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
  },
  filters: {
    currency(value) {
      const num = parseFloat(value || 0);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
  },
};
</script>